import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateQueryStatusRequestSchema,
  type CreateQueryStatusRequest,
  type QueryStatusDetail,
  type QueryStatusListItem,
  UpdateQueryStatusRequestSchema,
  type UpdateQueryStatusRequest,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
  UzTextarea,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateQueryStatus, useUpdateQueryStatus } from '../use-query-statuses';

import { deriveCode, useAutoDeriveCode } from '@/components/master-code-field';
import { StageColorField } from '@/components/StageColorField';

interface QueryStatusFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  queryStatus?: QueryStatusDetail | QueryStatusListItem | null;
}

const CREATE_DEFAULTS: CreateQueryStatusRequest = {
  code: '',
  name: '',
  description: '',
  bucket: '',
  color: null,
  isWon: false,
  isLost: false,
  sortOrder: 0,
  isActive: true,
};

/**
 * Field set shared by create + edit. Sub-statuses live on their own
 * master surface (Query Sub Statuses) - this form only edits the
 * top-level Query Status row.
 */
const QueryStatusFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Name"
          placeholder="e.g. Quoted"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="bucket">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Bucket"
          placeholder="e.g. OPEN"
          hint="Groups statuses for funnel rollups (e.g. OPEN, WON, LOST)."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <StageColorField disabled={pending} />
    <UzFormField name="description">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Description"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isWon">
      {({ field }) => (
        <UzSwitch
          label="Won"
          description="Mark this as a terminal won outcome."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isLost">
      {({ field }) => (
        <UzSwitch
          label="Lost"
          description="Mark this as a terminal lost outcome."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? '' : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive statuses are hidden from lead forms."
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const QueryStatusFormDrawer = ({
  open,
  onOpenChange,
  mode,
  queryStatus,
}: QueryStatusFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createQueryStatus = useCreateQueryStatus();
  const updateQueryStatus = useUpdateQueryStatus();
  const pending = createQueryStatus.isPending || updateQueryStatus.isPending;

  const createForm = useForm<CreateQueryStatusRequest>({
    resolver: zodResolver(CreateQueryStatusRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateQueryStatusRequest>({
    resolver: zodResolver(UpdateQueryStatusRequestSchema),
    defaultValues: {},
  });

  // Without this the hidden `code` stays '' and zod rejects the create
  // payload before the submit handler can derive it. Hook is a no-op in
  // edit mode (`code` is read-only post-create anyway).
  useAutoDeriveCode(createForm, isCreate);

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (queryStatus) {
      // `code` is intentionally omitted - it's the stable machine
      // identifier and is read-only post-create.
      updateForm.reset({
        name: queryStatus.name,
        description: 'description' in queryStatus ? (queryStatus.description ?? '') : '',
        bucket: queryStatus.bucket ?? '',
        color: queryStatus.color ?? null,
        isWon: queryStatus.isWon,
        isLost: queryStatus.isLost,
        sortOrder: queryStatus.sortOrder,
        isActive: queryStatus.isActive,
      });
    }
  }, [open, isCreate, queryStatus, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    // `code` is derived from the name (hidden field, populated by the
    // useAutoDeriveCode hook above so zod accepts it). We re-derive
    // here too as a belt-and-braces guard against a stale form state.
    await createQueryStatus.mutateAsync({
      ...values,
      code: deriveCode(values.name),
      bucket: values.bucket?.trim() ? values.bucket : null,
      description: values.description?.trim() ? values.description : null,
    });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!queryStatus) return;
    await updateQueryStatus.mutateAsync({
      id: queryStatus.id,
      payload: {
        ...values,
        bucket: values.bucket?.trim() ? values.bucket : null,
        description: values.description?.trim() ? values.description : null,
      },
    });
    onOpenChange(false);
  });

  const fields = <QueryStatusFields pending={pending} />;

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add query status' : `Edit ${queryStatus?.name ?? 'query status'}`}
      description="The name is what agents see."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
